<template>
  <v-card>
    <v-card-title>Employees</v-card-title>
    <v-card-text>
      <v-fade-transition>
        <v-alert
          v-show="actionAlert"
          border="left"
          color="success"
          dark
          text
          dismissible
        >
          {{ actionAlertMessage }}
        </v-alert>
      </v-fade-transition>
    </v-card-text>
    <v-card-text class="d-flex align-center flex-wrap pb-0">
      <!-- search -->
      <v-text-field
        v-model="searchQuery"
        placeholder="Search"
        outlined
        hide-details
        dense
        clearable
        class="user-search me-3 mb-4"
        @keyup.enter="fetchEmployees"
        @click:clear="clearSearch"
      >
      </v-text-field>

      <v-spacer></v-spacer>

      <div class="d-flex align-center flex-wrap">
        <v-btn
          v-if="$store.getters['auth/can'](['create employees'])"
          color="primary"
          class="mb-4 me-3"
          @click="$router.push({name: 'employees-create'})"
        >
          <v-icon>{{ icons.mdiPlus }}</v-icon>
          <span>Add New Employee</span>
        </v-btn>

        <!-- <v-btn
          color="secondary"
          outlined
          class="mb-4"
          disabled
        >
          <v-icon
            size="17"
            class="me-1"
          >
            {{ icons.mdiExportVariant }}
          </v-icon>
          <span>Export</span>
        </v-btn> -->
      </div>
    </v-card-text>
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="employees"
        :options.sync="options"
        :server-items-length="totalEmployees"
        :footer-props="footerProps"
        @page-count="pageCount = $event"
      >
        <!-- name -->
        <template #[`item.first_name`]="{ item }">
          <div class="d-flex align-center">
            <v-avatar
              :color="item.avatar ? '' : 'primary'"
              :class="item.avatar ? '' : 'v-avatar-light-bg primary--text'"
              size="32"
            >
              <span
                class="font-weight-medium"
              >{{ avatarText(item.first_name + ' ' + item.last_name ) }}</span>
            </v-avatar>

            <div class="d-flex flex-column ms-3">
              <router-link
                :to="{ name : 'employees-edit', params : { id : item.id } }"
                class="text--primary font-weight-semibold text-truncate cursor-pointer text-decoration-none"
              >
                {{ item.first_name }} {{ item.last_name }}
              </router-link>
            </div>
          </div>
        </template>

        <!-- is_active -->
        <template #[`item.is_active`]="{item}">
          <v-chip
            small
            :color="resolveUserStatusVariant(item.is_active)"
            :class="`${resolveUserStatusVariant(item.is_active)}--text`"
            class="v-chip-light-bg font-weight-semibold text-capitalize"
          >
            {{ item.is_active ? 'Active' : 'Inactive' }}
          </v-chip>
        </template>

        <!-- can_login -->
        <template #[`item.can_login`]="{item}">
          <v-chip
            small
            :color="resolveUserStatusVariant(item.can_login)"
            :class="`${resolveUserStatusVariant(item.can_login)}--text`"
            class="v-chip-light-bg font-weight-semibold text-capitalize"
          >
            {{ item.can_login ? 'Yes' : 'No' }}
          </v-chip>
        </template>

        <!-- roles -->
        <template #[`item.roles`]="{item}">
          <div
            v-for="(role, roleIdx) in item.roles"
            :key="roleIdx"
            class="d-flex align-center"
          >
            <v-avatar
              size="30"
              :color="resolveUserRoleVariant(role.name)"
              :class="`v-avatar-light-bg ${resolveUserRoleVariant(role.name)}--text me-3`"
            >
              <v-icon
                size="18"
                :color="resolveUserRoleVariant(role.name)"
              >
                {{ resolveUserRoleIcon(role.name) }}
              </v-icon>
            </v-avatar>
            <span class="text-capitalize">{{ role.name }}</span>
          </div>
        </template>

        <!-- actions -->
        <template #[`item.actions`]="{item}">
          <v-menu
            bottom
            left
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                v-if="$store.getters['auth/can'](['edit employees'])"
                @click="$router.push({ name: 'employees-edit', params: { id: item.id } })"
              >
                <v-list-item-title>
                  <v-icon
                    size="20"
                    class="me-2"
                  >
                    {{ icons.mdiPencilOutline }}
                  </v-icon>
                  <span>Edit</span>
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="$store.getters['auth/can'](['delete employees'])"
                @click="deleteItemConfirm(item)"
              >
                <v-list-item-title>
                  <v-icon
                    size="20"
                    class="me-2"
                  >
                    {{ icons.mdiDeleteOutline }}
                  </v-icon>
                  <span>Delete</span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card-text>
    <!-- dialog -->
    <v-dialog
      v-model="dialogDelete"
      max-width="500px"
    >
      <v-card>
        <v-card-title>
          Are you sure you want to delete this item?
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            outlined
            @click="dialogDelete = !dialogDelete"
          >
            Cancel
          </v-btn>
          <v-btn
            color="success"
            @click="deleteItem"
          >
            OK
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  mdiSquareEditOutline,
  mdiDotsVertical,
  mdiPlus,
  mdiExportVariant,
  mdiPencilOutline,
  mdiDeleteOutline,
} from '@mdi/js'
import { onMounted, ref, watch } from '@vue/composition-api'
import axios from '@axios'
import { avatarText } from '@core/utils/filter'
import useEmployees from './useEmployees'
import useUIResolver from '../useUIResolver'

const defaultAlertMessage = 'Employee successfully saved.'

export default {
  setup() {
    const employeeData = ref({})
    const actionAlert = ref(false)
    const actionAlertMessage = ref('')
    const searchQuery = ref('')
    const totalEmployees = ref(0)
    const footerProps = ref({
      itemsPerPageOptions: [15, 50, 100],
    })
    const options = ref({
      groupBy: [],
      groupDesc: [],
      itemsPerPage: 15,
      multiSort: false,
      mustSort: false,
      page: 1,
      sortBy: ['first_name'],
      sortDesc: [],
    })
    const dialogDelete = ref(false)

    onMounted(() => {
      actionAlertMessage.value = defaultAlertMessage
    })

    const { employees, getEmployees } = useEmployees()
    const {
      resolveUserStatusVariant, resolveUserRoleVariant, resolveUserRoleIcon, employeeTableHeaders,
    } = useUIResolver()

    const fetchEmployees = () => {
      getEmployees({ ...options.value, ...{ searchQuery: searchQuery.value } }).then(response => {
        const { total } = response.data
        totalEmployees.value = total
        options.value.itemsPerPage = response.data.per_page
      })
    }

    const clearSearch = () => {
      searchQuery.value = null
      fetchEmployees()
    }

    const saveEmployee = () => {
      actionAlertMessage.value = defaultAlertMessage
      actionAlert.value = true
      fetchEmployees()
    }

    const deleteItem = () => {
      axios.delete(`/users/${employeeData.value.id}`).then(response => {
        actionAlertMessage.value = 'Employee successfully deleted.'
        actionAlert.value = true

        // hide confirm alert
        dialogDelete.value = false
        fetchEmployees()
      })
    }

    const deleteItemConfirm = item => {
      employeeData.value = item
      dialogDelete.value = true
    }

    watch(options, fetchEmployees)

    watch(actionAlert, val => {
      if (val) {
        setTimeout(() => {
          actionAlert.value = false
        }, 3000)
      }
    })

    return {
      avatarText,
      dialogDelete,
      actionAlert,
      actionAlertMessage,
      employeeData,
      headers: employeeTableHeaders(),
      searchQuery,

      totalEmployees,
      footerProps,
      options,

      // useUIResolver
      resolveUserStatusVariant,
      resolveUserRoleVariant,
      resolveUserRoleIcon,

      // useEmployees
      employees,
      getEmployees,

      // functions
      clearSearch,
      saveEmployee,
      fetchEmployees,

      deleteItem,
      deleteItemConfirm,

      // icons
      icons: {
        mdiSquareEditOutline,
        mdiDotsVertical,
        mdiPlus,
        mdiExportVariant,
        mdiPencilOutline,
        mdiDeleteOutline,
      },
    }
  },
}
</script>
